import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Unique identifier for a topic (focus area)
 * Format: "groupIndex:areaIndex"
 */
export type TopicId = string;

/**
 * Topic information stored in priority selection
 */
export interface SelectedTopic {
  topicId: TopicId;
  groupIndex: number;
  areaIndex: number;
  title: string;
  groupTitle: string;
  label: string; // "Enabler" or "Blocker" or group title
}

/**
 * Contextual selection guidance tracked to avoid repeating onboarding hints.
 * Submission confirmations are action feedback and are intentionally not tracked here.
 */
export type ToastType = "firstStar" | "allStarsUsed";

interface PriorityState {
  /**
   * The canvas ID these priorities belong to
   */
  canvasId: string | null;

  /**
   * Array of currently selected topic IDs
   */
  selectedTopicIds: TopicId[];

  /**
   * Map of topic IDs to their metadata
   */
  selectedTopics: Record<TopicId, SelectedTopic>;

  /**
   * Whether the user has submitted their priorities
   * (persisted per canvas)
   */
  isSubmitted: boolean;

  /**
   * Optional comments for each selected topic
   * (persisted per canvas)
   */
  comments: Record<TopicId, string>;

  /**
   * Track which contextual guidance toasts have been shown this session
   * (NOT persisted - resets on page reload)
   */
  shownToasts: Record<ToastType, boolean>;

  /**
   * Whether the "Your Priorities" modal is open
   */
  isModalOpen: boolean;

  /**
   * Maximum number of topics a user can select as priorities for this canvas.
   */
  maxSelections: number;

  /**
   * Message to display in the modal (used when limit is reached)
   */
  modalMessage?: string;

  /**
   * Set the current canvas ID and clear selections if it changed
   */
  setCanvasId: (canvasId: string) => void;

  /**
   * Toggle a topic's priority status
   * Returns true if selection was successful, false if limit reached
   */
  toggleTopic: (topic: SelectedTopic) => boolean;

  /**
   * Remove a topic from priorities
   */
  removeTopic: (topicId: TopicId) => void;

  /**
   * Check if a topic is selected
   */
  isTopicSelected: (topicId: TopicId) => boolean;

  /**
   * Get the number of selected topics
   */
  getSelectedCount: () => number;

  /**
   * Check if the selection limit has been reached
   */
  isLimitReached: () => boolean;

  /**
   * Set a comment for a specific topic
   */
  setComment: (topicId: TopicId, comment: string) => void;

  /**
   * Set the maximum number of selections allowed.
   */
  setMaxSelections: (maxSelections: number) => void;

  /**
   * Hydrate selections + comments from the server (does not affect submitted state).
   */
  hydrateFromServer: (data: {
    selectedTopicIds: TopicId[];
    selectedTopics: Record<TopicId, SelectedTopic>;
    comments: Record<TopicId, string>;
  }) => void;

  /**
   * Submit the priorities (marks as submitted)
   */
  submit: () => void;

  /**
   * Mark priorities as not submitted (e.g., when user makes changes)
   */
  unsubmit: () => void;

  /**
   * Mark a toast as shown (to avoid showing it again this session)
   */
  markToastShown: (toast: ToastType) => void;

  /**
   * Check if a toast has been shown this session
   */
  hasToastBeenShown: (toast: ToastType) => boolean;

  /**
   * Open the priorities modal
   */
  openModal: (message?: string) => void;

  /**
   * Close the priorities modal
   */
  closeModal: () => void;

  /**
   * Clear all selections (for testing/reset)
   */
  clearAll: () => void;
}

/**
 * Create a unique topic ID from group and area indices
 */
export function createTopicId(groupIndex: number, areaIndex: number): TopicId {
  return `${groupIndex}:${areaIndex}`;
}

/**
 * Parse a topic ID into group and area indices.
 * Assumes the topicId is a valid TopicId format.
 * For parsing potentially invalid input (e.g., from API responses), use parseTopicIdSafe instead.
 */
export function parseTopicId(topicId: TopicId): {
  groupIndex: number;
  areaIndex: number;
} {
  const [groupIndex, areaIndex] = topicId.split(":").map(Number);
  return { groupIndex, areaIndex };
}

/**
 * Safe parser for topic IDs that handles potentially invalid input (e.g., from API responses).
 * Returns null if the topic ID cannot be parsed.
 * For validated TopicId types, use parseTopicId instead.
 *
 * @param topicId - Topic ID string in format "groupIndex:areaIndex" (may be invalid)
 * @returns Object with groupIndex and areaIndex, or null if parsing fails
 */
export function parseTopicIdSafe(topicId: string): { groupIndex: number; areaIndex: number } | null {
  const parts = topicId.split(":");
  if (parts.length !== 2) return null;
  const groupIndex = Number(parts[0]);
  const areaIndex = Number(parts[1]);
  if (Number.isNaN(groupIndex) || Number.isNaN(areaIndex)) return null;
  return { groupIndex, areaIndex };
}

/**
 * Initial state for shownToasts (reset each session)
 */
const initialShownToasts: Record<ToastType, boolean> = {
  firstStar: false,
  allStarsUsed: false,
};

export const usePriorityStore = create<PriorityState>()(
  persist(
    (set, get) => ({
      canvasId: null,
      selectedTopicIds: [],
      selectedTopics: {},
      isSubmitted: false,
      comments: {},
      shownToasts: { ...initialShownToasts },
      isModalOpen: false,
      maxSelections: 3,
      modalMessage: undefined,

      setCanvasId: (canvasId: string) => {
        const state = get();
        // If canvas changed, clear all selections and reset state
        if (state.canvasId && state.canvasId !== canvasId) {
          set({
            canvasId,
            selectedTopicIds: [],
            selectedTopics: {},
            isSubmitted: false,
            comments: {},
            shownToasts: { ...initialShownToasts },
          });
        } else if (!state.canvasId) {
          // First time setting canvas ID
          set({ canvasId });
        }
      },

      toggleTopic: (topic: SelectedTopic) => {
        const state = get();
        const isSelected = state.selectedTopicIds.includes(topic.topicId);

        if (isSelected) {
          // Remove the topic
          const newComments = { ...state.comments };
          delete newComments[topic.topicId];

          set({
            selectedTopicIds: state.selectedTopicIds.filter((id) => id !== topic.topicId),
            selectedTopics: Object.fromEntries(
              Object.entries(state.selectedTopics).filter(([id]) => id !== topic.topicId),
            ),
            comments: newComments,
            // If already submitted, mark as needing resubmission
            isSubmitted: false,
          });
          return true;
        } else {
          // Check if limit is reached
          if (state.selectedTopicIds.length >= state.maxSelections) {
            // Open modal with limit message
            set({
              isModalOpen: true,
              modalMessage: `You can select up to ${state.maxSelections} topics. Deselect one to add another.`,
            });
            return false;
          }

          // Add the topic
          set({
            selectedTopicIds: [...state.selectedTopicIds, topic.topicId],
            selectedTopics: {
              ...state.selectedTopics,
              [topic.topicId]: topic,
            },
            // If already submitted, mark as needing resubmission
            isSubmitted: false,
          });
          return true;
        }
      },

      removeTopic: (topicId: TopicId) => {
        const state = get();
        const newComments = { ...state.comments };
        delete newComments[topicId];

        set({
          selectedTopicIds: state.selectedTopicIds.filter((id) => id !== topicId),
          selectedTopics: Object.fromEntries(Object.entries(state.selectedTopics).filter(([id]) => id !== topicId)),
          comments: newComments,
          // If already submitted, mark as needing resubmission
          isSubmitted: false,
        });
      },

      isTopicSelected: (topicId: TopicId) => {
        return get().selectedTopicIds.includes(topicId);
      },

      getSelectedCount: () => {
        return get().selectedTopicIds.length;
      },

      isLimitReached: () => {
        const state = get();
        return state.selectedTopicIds.length >= state.maxSelections;
      },

      setComment: (topicId: TopicId, comment: string) => {
        const state = get();
        set({
          comments: {
            ...state.comments,
            [topicId]: comment,
          },
        });
      },

      setMaxSelections: (maxSelections: number) => {
        // Guard against invalid values; keep minimum 1 to avoid confusing UX.
        const safeMax = Number.isFinite(maxSelections) && maxSelections > 0 ? maxSelections : 1;
        set({ maxSelections: safeMax });
      },

      hydrateFromServer: ({ selectedTopicIds, selectedTopics, comments }) => {
        set({
          selectedTopicIds,
          selectedTopics,
          comments,
        });
      },

      submit: () => {
        set({ isSubmitted: true });
      },

      unsubmit: () => {
        set({ isSubmitted: false });
      },

      markToastShown: (toast: ToastType) => {
        const state = get();
        set({
          shownToasts: {
            ...state.shownToasts,
            [toast]: true,
          },
        });
      },

      hasToastBeenShown: (toast: ToastType) => {
        return get().shownToasts[toast] || false;
      },

      openModal: (message?: string) => {
        set({
          isModalOpen: true,
          modalMessage: message,
        });
      },

      closeModal: () => {
        set({
          isModalOpen: false,
          modalMessage: undefined,
        });
      },

      clearAll: () => {
        set({
          selectedTopicIds: [],
          selectedTopics: {},
          isSubmitted: false,
          comments: {},
          shownToasts: { ...initialShownToasts },
        });
      },
    }),
    {
      name: "canvas-priorities-storage", // localStorage key
      // Persist canvas-specific data (NOT shownToasts - that's session-only)
      partialize: (state) => ({
        canvasId: state.canvasId,
        selectedTopicIds: state.selectedTopicIds,
        selectedTopics: state.selectedTopics,
        isSubmitted: state.isSubmitted,
        comments: state.comments,
        maxSelections: state.maxSelections,
      }),
    },
  ),
);
